import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * RGPD-compliant cookie consent banner with granular categories.
 * Stores consent in localStorage under `cookie_consent_v1`:
 *   { essential: true, analytics: bool, marketing: bool, ts: ISODate }
 * Other scripts (analytics, pixels, clarity) must read getConsent() before firing.
 */
const STORAGE_KEY = "cookie_consent_v1";

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setConsent(consent) {
  const payload = { essential: true, ...consent, ts: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: payload }));
  return payload;
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      // Wait a beat so the page paints first
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Expose a global opener so the footer "Manage cookies" link can re-open the banner
  useEffect(() => {
    window.openCookieBanner = () => {
      const c = getConsent();
      if (c) {
        setAnalytics(!!c.analytics);
        setMarketing(!!c.marketing);
      }
      setShowDetails(true);
      setOpen(true);
    };
  }, []);

  function acceptAll() {
    setConsent({ analytics: true, marketing: true });
    setOpen(false);
  }
  function rejectAll() {
    setConsent({ analytics: false, marketing: false });
    setOpen(false);
  }
  function savePrefs() {
    setConsent({ analytics, marketing });
    setOpen(false);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Bandeau de consentement aux cookies"
      data-testid="cookie-banner"
      className="fixed bottom-0 inset-x-0 z-[9999] p-4 md:p-6"
    >
      <div className="max-w-5xl mx-auto bg-white border border-ink/10 shadow-2xl rounded-2xl p-5 md:p-7">
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="font-display text-xl md:text-2xl mb-2">Nous respectons votre vie privée</h2>
            <p className="text-sm text-ink-soft leading-relaxed">
              Nous utilisons des cookies pour assurer le bon fonctionnement du site, mesurer son audience et
              améliorer votre expérience. Vous pouvez accepter, refuser ou personnaliser votre choix à tout moment.
              <Link to="/rgpd" className="underline ms-1 text-pedablue">En savoir plus</Link>
            </p>
          </div>

          {showDetails && (
            <div className="space-y-3 border-t border-ink/10 pt-4">
              <ConsentRow
                title="Cookies essentiels"
                desc="Indispensables au fonctionnement du site (panier, session, langue)."
                checked
                disabled
                testid="consent-essential"
              />
              <ConsentRow
                title="Cookies analytiques"
                desc="Google Analytics 4, Microsoft Clarity — statistiques anonymisées de visite."
                checked={analytics}
                onChange={setAnalytics}
                testid="consent-analytics"
              />
              <ConsentRow
                title="Cookies marketing"
                desc="Meta Pixel, TikTok Pixel — mesure de l'efficacité publicitaire."
                checked={marketing}
                onChange={setMarketing}
                testid="consent-marketing"
              />
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={rejectAll}
              className="px-4 py-2 rounded-full text-sm border border-ink/20 hover:bg-ivory-200"
              data-testid="consent-reject-all"
            >
              Tout refuser
            </button>
            {!showDetails ? (
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                className="px-4 py-2 rounded-full text-sm border border-ink/20 hover:bg-ivory-200"
                data-testid="consent-customize"
              >
                Personnaliser
              </button>
            ) : (
              <button
                type="button"
                onClick={savePrefs}
                className="px-4 py-2 rounded-full text-sm border border-ink/20 hover:bg-ivory-200"
                data-testid="consent-save"
              >
                Enregistrer mes choix
              </button>
            )}
            <button
              type="button"
              onClick={acceptAll}
              className="px-5 py-2 rounded-full text-sm bg-ink text-white hover:bg-ink/90"
              data-testid="consent-accept-all"
            >
              Tout accepter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConsentRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        data-testid={testid}
        className="mt-1 h-4 w-4 accent-pedablue disabled:opacity-50"
      />
      <div className="flex-1">
        <p className="font-medium text-sm">{title}{disabled && <span className="text-ink/40 text-xs ms-2">(toujours actif)</span>}</p>
        <p className="text-xs text-ink-soft">{desc}</p>
      </div>
    </div>
  );
}
